/**
 * CapabilitiesMatrix.jsx · v2.7.45+E14 (Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Per-position permission matrix UI for the Developer Email Admin
 * dashboard. Renders a positions × capabilities grid grouped by
 * capability domain. Checkboxes toggle in a local draft state; the
 * developer clicks "Save matrix" to flush the entire draft via
 * /admin/capabilities/bulk in one shot.
 *
 * Layout:
 *   ┌───────────────┬──── EMAIL ────┬──── FINANCE ────┬── DISPATCH ──┬── …
 *   │ Position name │ □ send_ext    │ □ approve_small │ □ assign     │ …
 *   ├───────────────┼───────────────┼─────────────────┼──────────────┼──
 *   │ Taxi Driver I │ ☑ send_ext    │ □ approve_small │ □ assign     │ …
 *
 * The 47 position rows can get tall — we virtualize lightly by keeping
 * the header sticky and shipping a max-height + overflow-y on the
 * outer scroll container.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Save, Loader2, RotateCcw, Search, ShieldCheck, Sparkles, Info, X,
} from "lucide-react";
import { POSITION_CATALOG, POSITION_TO_TIER } from "@/lib/positions";

const TIER_PILL = {
  driver:            "bg-slate-200 text-slate-700 dark:bg-zinc-800 dark:text-zinc-300",
  team_leader:       "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  dispatcher:        "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
  supervisor:        "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  operating_partner: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  developer:         "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300",
  admin:             "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
};

const DOMAIN_ACCENT = {
  email:     "border-sky-300 bg-sky-50/40 dark:bg-sky-950/15",
  finance:   "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/15",
  dispatch:  "border-amber-300 bg-amber-50/40 dark:bg-amber-950/15",
  safety:    "border-rose-300 bg-rose-50/40 dark:bg-rose-950/15",
  marketing: "border-violet-300 bg-violet-50/40 dark:bg-violet-950/15",
};

export default function CapabilitiesMatrix() {
  const [data, setData] = useState({ catalog: [], domains: [], matrix: {} });
  const [draft, setDraft] = useState({});  // {position: Set<capability>}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [posFilter, setPosFilter] = useState("");
  // v2.7.45+E15 · "Who can do this?" capability audit search. When a
  // value is set the matrix highlights every position whose effective
  // capability list contains the term (matched against capability key
  // OR human label). The position rows are NOT filtered out — they
  // just get dimmed to ~30 % opacity so the auditor sees the full
  // hierarchy at a glance with the matching positions standing out.
  const [capAudit, setCapAudit] = useState("");

  const auditingCap = useMemo(() => {
    const term = capAudit.trim().toLowerCase();
    if (!term) return null;
    // Match against capability key OR human label
    return (data.catalog || []).filter((c) =>
      c.key.toLowerCase().includes(term) ||
      c.label.toLowerCase().includes(term)
    ).map((c) => c.key);
  }, [capAudit, data.catalog]);

  const positionMatchesAudit = useCallback((pos) => {
    if (!auditingCap || auditingCap.length === 0) return true;
    const set = draft[pos] || new Set();
    return auditingCap.some((k) => set.has(k));
  }, [auditingCap, draft]);

  const auditingCount = useMemo(() => {
    if (!auditingCap || auditingCap.length === 0) return 0;
    return POSITION_CATALOG.filter((p) => positionMatchesAudit(p)).length;
  }, [auditingCap, positionMatchesAudit]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await adminApi.get("/admin/capabilities");
      setData({
        catalog: payload?.catalog || [],
        domains: payload?.domains || [],
        matrix:  payload?.matrix  || {},
      });
      // Rebuild draft sets from server payload
      const next = {};
      for (const p of POSITION_CATALOG) {
        next[p] = new Set(payload?.matrix?.[p] || []);
      }
      setDraft(next);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Dirty detection: any draft set diverges from the server matrix
  const dirtyPositions = useMemo(() => {
    const out = new Set();
    for (const p of POSITION_CATALOG) {
      const server = (data.matrix?.[p] || []).slice().sort().join("|");
      const local = Array.from(draft[p] || []).slice().sort().join("|");
      if (server !== local) out.add(p);
    }
    return out;
  }, [data.matrix, draft]);

  const filteredPositions = useMemo(() => {
    if (!posFilter.trim()) return POSITION_CATALOG;
    const t = posFilter.trim().toLowerCase();
    return POSITION_CATALOG.filter((p) => p.toLowerCase().includes(t));
  }, [posFilter]);

  const toggle = (pos, cap) => {
    setDraft((d) => {
      const next = { ...d };
      const set = new Set(next[pos] || []);
      if (set.has(cap)) set.delete(cap); else set.add(cap);
      next[pos] = set;
      return next;
    });
  };

  const saveAll = async () => {
    if (dirtyPositions.size === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const rows = {};
      for (const p of dirtyPositions) rows[p] = Array.from(draft[p] || []);
      const r = await adminApi.post("/admin/capabilities/bulk", { rows });
      toast.success(`Saved · ${r.data?.updated || 0} position${r.data?.updated === 1 ? "" : "s"} updated`);
      reload();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetDraft = () => {
    const next = {};
    for (const p of POSITION_CATALOG) {
      next[p] = new Set(data.matrix?.[p] || []);
    }
    setDraft(next);
    toast.success("Reverted to last saved");
  };

  // Helper — "give this position all caps in the domain"
  const grantDomain = (pos, domainKey) => {
    const set = new Set(draft[pos] || []);
    for (const c of data.catalog) {
      if (c.domain === domainKey) set.add(c.key);
    }
    setDraft((d) => ({ ...d, [pos]: set }));
  };
  const revokeDomain = (pos, domainKey) => {
    const set = new Set(draft[pos] || []);
    for (const c of data.catalog) {
      if (c.domain === domainKey) set.delete(c.key);
    }
    setDraft((d) => ({ ...d, [pos]: set }));
  };

  return (
    <div className="space-y-3" data-testid="capabilities-matrix">
      {/* Info banner */}
      <div className="text-xs text-slate-600 dark:text-zinc-400 bg-violet-50 dark:bg-violet-900/15 border border-violet-200 dark:border-violet-900/40 rounded p-3 leading-relaxed flex gap-2 items-start">
        <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5"/>
        <div>
          Grant fine-grained permission flags per position. A user&apos;s effective capability set is the <b>union</b> of every position they hold —
          a Senior Taxi Driver who&apos;s also a Customer Support Lead gets both rows&apos; caps. Toggle checkboxes, then click <b>Save matrix</b> to flush all changes in one shot.
        </div>
      </div>

      {/* Header controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"/>
          <Input value={posFilter} onChange={(e) => setPosFilter(e.target.value)}
            placeholder="Filter positions…" className="pl-8 h-8 text-xs" data-testid="caps-position-filter"/>
        </div>
        {/* v2.7.45+E15 · Who can do this? audit search */}
        <div className="relative flex-1 min-w-[260px]">
          <ShieldCheck className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-violet-500"/>
          <Input value={capAudit} onChange={(e) => setCapAudit(e.target.value)}
            placeholder="Who can do this? (e.g. finance.refunds or 'approve contracts')"
            className="pl-8 h-8 text-xs border-violet-300 dark:border-violet-700/50"
            data-testid="caps-audit-search"/>
          {capAudit && (
            <button type="button" onClick={() => setCapAudit("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-zinc-100"
              aria-label="Clear audit search">
              <X className="h-3 w-3"/>
            </button>
          )}
        </div>
        {auditingCap && auditingCap.length > 0 && (
          <div className="text-[10px] uppercase tracking-widest font-bold text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 px-2 py-1 rounded" data-testid="caps-audit-count">
            {auditingCount} position{auditingCount === 1 ? "" : "s"} grant{auditingCount === 1 ? "s" : ""} this
          </div>
        )}
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500">
          {dirtyPositions.size > 0
            ? <span className="text-amber-700 dark:text-amber-300">{dirtyPositions.size} unsaved row{dirtyPositions.size === 1 ? "" : "s"}</span>
            : "All saved"}
        </div>
        <Button size="sm" variant="outline" onClick={resetDraft} disabled={dirtyPositions.size === 0 || saving}
          className="h-8 gap-1 text-xs" data-testid="caps-reset">
          <RotateCcw className="h-3 w-3"/>Reset
        </Button>
        <Button size="sm" onClick={saveAll} disabled={dirtyPositions.size === 0 || saving}
          className="bg-violet-600 hover:bg-violet-500 text-white h-8 gap-1.5 text-xs" data-testid="caps-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Save className="h-3.5 w-3.5"/>}
          Save matrix
        </Button>
      </div>

      {loading && (
        <div className="text-sm text-slate-500 py-6 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin"/>Loading matrix…
        </div>
      )}

      {!loading && (
        <div className="overflow-auto border border-slate-200 dark:border-zinc-800 rounded-lg" style={{ maxHeight: "calc(100vh - 280px)" }}>
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-white dark:bg-zinc-950 z-10 shadow-sm">
              <tr>
                <th className="text-left p-2 border-b border-r border-slate-200 dark:border-zinc-800 sticky left-0 bg-white dark:bg-zinc-950 min-w-[210px] z-20">
                  Position
                </th>
                {data.domains.map((d) => {
                  const caps = data.catalog.filter((c) => c.domain === d.key);
                  return (
                    <th key={d.key} colSpan={caps.length}
                      className={`text-center p-2 border-b border-l border-slate-200 dark:border-zinc-800 ${DOMAIN_ACCENT[d.key] || ""}`}>
                      <div className="text-[10px] uppercase tracking-widest font-bold">{d.label}</div>
                    </th>
                  );
                })}
              </tr>
              <tr>
                <th className="p-2 border-b border-r border-slate-200 dark:border-zinc-800 sticky left-0 bg-white dark:bg-zinc-950 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 z-20">
                  Capability →
                </th>
                {data.domains.map((d) =>
                  data.catalog.filter((c) => c.domain === d.key).map((c) => (
                    <th key={c.key} className="p-1.5 border-b border-l border-slate-200 dark:border-zinc-800 align-bottom text-center">
                      <div className="text-[10px] font-bold text-slate-700 dark:text-zinc-300 leading-tight" title={c.description}>
                        {c.label}
                      </div>
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {filteredPositions.map((pos) => {
                const tier = POSITION_TO_TIER[pos] || "driver";
                const isDirty = dirtyPositions.has(pos);
                const matchesAudit = positionMatchesAudit(pos);
                const auditing = !!auditingCap && auditingCap.length > 0;
                const rowClass = auditing
                  ? (matchesAudit
                      ? "bg-violet-50 dark:bg-violet-900/20 ring-1 ring-violet-300 dark:ring-violet-700"
                      : "opacity-30")
                  : (isDirty ? "bg-amber-50/50 dark:bg-amber-900/10" : "even:bg-slate-50/50 dark:even:bg-zinc-900/30");
                return (
                  <tr key={pos} className={rowClass} data-testid={`caps-row-${pos.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="p-2 border-r border-slate-200 dark:border-zinc-800 sticky left-0 bg-inherit min-w-[210px] z-10">
                      <div className="flex items-center justify-between gap-1.5">
                        <span className="font-bold truncate">{pos}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${TIER_PILL[tier] || ""} flex-shrink-0`}>
                          {tier.replace("_", " ")}
                        </span>
                      </div>
                    </td>
                    {data.domains.map((d) => {
                      const caps = data.catalog.filter((c) => c.domain === d.key);
                      const allOn = caps.every((c) => (draft[pos] || new Set()).has(c.key));
                      return caps.map((c, idx) => (
                        <td key={c.key} className={`p-1.5 border-l border-slate-200 dark:border-zinc-800 text-center ${DOMAIN_ACCENT[d.key] || ""}`}>
                          <input
                            type="checkbox"
                            checked={(draft[pos] || new Set()).has(c.key)}
                            onChange={() => toggle(pos, c.key)}
                            className="h-3.5 w-3.5 rounded border-slate-300 dark:border-zinc-700 text-violet-600 focus:ring-violet-500 cursor-pointer"
                            title={`${pos} · ${c.label} — ${c.description}`}
                            data-testid={`caps-cell-${pos.replace(/\s+/g, "-").toLowerCase()}-${c.key}`}
                            aria-label={`${c.label} for ${pos}`}
                          />
                          {/* Last cell of each domain row: tiny shortcut to flip the entire domain */}
                          {idx === caps.length - 1 && (
                            <div className="text-[8px] mt-0.5 opacity-60 hover:opacity-100">
                              <button type="button"
                                onClick={() => (allOn ? revokeDomain(pos, d.key) : grantDomain(pos, d.key))}
                                className="underline text-slate-500 dark:text-zinc-500" title={allOn ? `Clear all ${d.label} caps for ${pos}` : `Grant all ${d.label} caps to ${pos}`}>
                                {allOn ? "clear all" : "grant all"}
                              </button>
                            </div>
                          )}
                        </td>
                      ));
                    })}
                  </tr>
                );
              })}
              {filteredPositions.length === 0 && (
                <tr><td colSpan={(data.catalog?.length || 0) + 1} className="text-center py-6 text-xs italic text-slate-500">No positions match the filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      {!loading && (
        <div className="text-[10px] text-slate-500 dark:text-zinc-500 flex items-center gap-3 flex-wrap pt-2">
          <span className="flex items-center gap-1"><Info className="h-3 w-3"/>Hover any checkbox for the description.</span>
          <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3"/>Master admin holds every capability implicitly.</span>
        </div>
      )}
    </div>
  );
}
